import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';

// ----------------------------------------------------------------------

// Enhanced TextField with better styling and label behavior
const StyledTextField = styled(TextField)(({ theme, variant }) => ({
  '& .MuiInputLabel-root': {
    // Proper label positioning
    transform: 'translate(14px, 16px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: variant === 'filled' 
        ? 'translate(12px, 7px) scale(0.75)'
        : 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.09)' : 'transparent',
    '&.Mui-disabled': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.disabledBackground,
    },
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
    padding: variant === 'filled' ? '25px 12px 8px' : '16.5px 14px',
    '&.Mui-disabled': {
      WebkitTextFillColor: theme.palette.text.disabled,
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-disabled fieldset': {
      borderColor: theme.palette.action.disabled,
    },
  },
  '& .MuiFilledInput-root': {
    '&:before': {
      borderBottomColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.42)' : 'rgba(0, 0, 0, 0.42)',
    },
    '&:hover:before': {
      borderBottomColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
    },
    '&.Mui-disabled:before': {
      borderBottomStyle: 'solid',
    },
  },
}));

// ----------------------------------------------------------------------

/**
 * Enhanced React Hook Form TextField component with better label handling
 * Fixes label overlap issues and provides consistent styling
 */
export default function RHFTextFieldEnhanced({ 
  name, 
  helperText, 
  type, 
  color,
  variant = 'outlined',
  disabled = false,
  placeholder,
  multiline = false,
  margintop,
  rows,
  minRows,
  maxRows,
  InputProps,
  inputProps,
  InputLabelProps,
  shrinkLabel,
  onlyNumbers = false,
  ...other 
}) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Determine if label should shrink
        const shouldShrink = shrinkLabel !== undefined 
          ? shrinkLabel 
          : !!(field.value || field.value === 0 || disabled);

        return (
          <StyledTextField
            sx={{ mt: margintop }}
            {...field}
            fullWidth
            type={type}
            variant={variant}
            disabled={disabled}
            placeholder={placeholder}
            multiline={multiline}
            rows={rows}
            minRows={minRows}
            maxRows={maxRows}
            value={type === 'number' || (field.value === 0 || field.value === 'null' || field.value === 'undefined') ? '' : (field.value || '')}
            onChange={(event) => {
              let value = event.target.value;

              if (onlyNumbers) {
                value = value.replace(/\D+/g, '');
              }

              if (type === 'number') {
                field.onChange(value === '' ? '' : Number(value));
              } else {
                field.onChange(value);
              }
            }}
            error={!!error}
            helperText={error ? error?.message : helperText}
            InputLabelProps={{
              shrink: shouldShrink,
              ...InputLabelProps,
            }}
            InputProps={{
              ...InputProps,
            }}
            inputProps={{
              ...(onlyNumbers ? { inputMode: 'numeric', pattern: '[0-9]*' } : {}),
              ...inputProps,
            }}
            {...other}
          />
        );
      }}
    />
  );
}

RHFTextFieldEnhanced.propTypes = {
  name: PropTypes.string.isRequired,
  helperText: PropTypes.node,
  type: PropTypes.string,
  color: PropTypes.string,
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  shrinkLabel: PropTypes.bool,
  onlyNumbers: PropTypes.bool,
};

